import type {
  IncomeProtectionDAFormKeysType,
  IncomeProtectionDASettingDisplayKeysType,
  IncomeProtectionDASummaryDisplayKeysType,
  PrePaymentFormValuesType,
} from './types';

const settingFormLabels = {
  benefit_period_num_of_days: 'Benefit Period',
  waiting_period_num_of_days: 'Waiting Period (days)',
  start_date: 'Start Date',
  payment_frequency_num_of_days: 'Payment Frequency',
  average_weekly_income: 'Average Weekly Income',
  weekly_payment_limit: 'Weekly Payment Limit',
  weekly_income_percentage: 'Weekly Income Percentage',
  claimant_weekly_limit: 'Claimant Weekly Limit',
  payee_contact_id: 'Payee',
  payment_frequency_custom: 'Custom Payment Frequency (days)',
} as const satisfies Record<IncomeProtectionDAFormKeysType, string>;

const settingDisplayLabels = {
  benefit_period_num_of_days: 'Maximum Benefit Period',
  start_date: 'Start Date',
  payment_frequency_num_of_days: 'Payment Frequency',
  average_weekly_income: 'Average Weekly Income',
  weekly_payment_limit: 'Weekly Payment Limit',
  claimant_weekly_limit: 'Claimant Weekly Limit',
  payee_contact_id: 'Payee Contact',
} as const satisfies Record<IncomeProtectionDASettingDisplayKeysType, string>;

const summaryDisplayLabels = {
  exposure_number: 'Exposure',
  claimant_id: 'Claimant',
  total_paid: 'Total Paid',
  total_incurred: 'Total Incurred',
  days_paid: 'Weeks Paid',
  days_remaining: 'Weeks Remaining',
  medical_certificate_expiry_date: 'Medical Cert Expiry',
} as const satisfies Record<IncomeProtectionDASummaryDisplayKeysType, string>;

const prePaymentFormLabels = {
  service_start_date: 'Start Date',
  service_end_date: 'End Date',
  amount: 'Amount',
} as const satisfies Record<keyof PrePaymentFormValuesType, string>;

const TEXTS = {
  view: 'View',
  viewOrCreateSettings: 'View or Create Settings',
  settingsDialogTitle: 'Income Protection Payment Settings',
  noSettingsAndViewOnly: 'Payment settings have not yet been set for this exposure',
  eligibilityAndTiming: 'Eligibility and Timing',
  income: 'Income',
  settingFormLabels,
  cancel: 'Cancel',
  submitSettings: 'Set Settings',
  required: 'Required',
  cannotBeNegative: 'Negative values are not allowed',
  mustBeAnInteger: 'Must be a whole number',
  mustBeGreaterThan0: 'Must be greater than 0',
  mustBeBetween0And100: 'Must be between 0 and 100',
  custom: 'Custom',
  week: '7 Days',
  fortnight: '14 Days',
  inDays: 'In Days',
  inWeeks: 'In Weeks',
  benefitPeriodUnit: 'Benefit Period Unit',

  screenTitle: 'Income Protection - Damage Assessment',
  paymentSettings: 'Payment Settings',
  edit: 'Edit',
  settingDisplayLabels,
  valueAsDays: (value: number) => `${value} ${value === 1 ? 'Day' : 'Days'}`,
  emptyValue: '-',
  notAvailable: 'N/A',

  summaryDisplayLabels,

  paymentsSectionTitle: 'Payments',
  addPayment: 'Add Payment',
  prePaymentFormTitle: 'Review payment dates and amount',
  prePaymentFormLabels,
  submitPrePayment: 'Proceed to Payment',
  cannotBeEarlierThanStartDate: 'Must not be earlier than start date',
  totalDays: (days: number) => `Total of ${days} ${days === 1 ? 'day' : 'days'}`,
  startDateDoesNotMatchExpectedStartDate: 'Unexpected start date',
  startDateDoesNotMatchExpectedStartDateNote: (expectedStartDate: string, actualStartDate: string) =>
    `The expected start date is ${expectedStartDate}, but this payment is set to begin on ${actualStartDate}`,
  paymentPeriodIsLongerThenSetting: 'Payment period warning',
  paymentPeriodIsLongerThenSettingNote: (settingDays: number, paymentPeriodDays: number) =>
    `This payment covers ${paymentPeriodDays} ${paymentPeriodDays === 1 ? 'day' : 'days'}, which is longer than the configured payment frequency of ${settingDays} ${settingDays === 1 ? 'day' : 'days'}`,
  benefitPeriodIsLongerThenSetting: 'Benefit period warning',
  benefitPeriodIsLongerThenSettingNote: (settingPeriod: string, paymentPeriodSurplus: string) =>
    `This payment would exceed the benefit period of ${settingPeriod} by ${paymentPeriodSurplus}`,
  medicalCertificateExpiryDateIsBeforeServiceEndDate: 'Medical certificate expiry warning',
  medicalCertificateExpiryDateIsBeforeServiceEndDateNote: (
    medicalCertificateExpiryDate: string,
    serviceEndDate: string
  ) =>
    `The medical certificate expires on ${medicalCertificateExpiryDate}, but the payment end date is set to ${serviceEndDate}`,
  amountLargerThanClaimantWeeklyLimit: 'Weekly limit warning',
  amountLargerThanClaimantWeeklyLimitNote: (
    claimantWeeklyLimit: string | number,
    overLimit: string | number,
    overOneWeek: boolean
  ) =>
    overOneWeek
      ? `This payment would exceed the claimant’s weekly limit of ${claimantWeeklyLimit} by ${overLimit}`
      : `When prorated on a weekly basis, this payment would exceed the claimant’s weekly limit of ${claimantWeeklyLimit} by ${overLimit}`,
  setNotification: 'Set Notification',
  notificationDisabledWarning:
    'This notification type is disabled for the organization and cannot be created or edited. Please contact your administrator to enable it.',
  notificationSetFor: (date: string) => `Notification set for ${date}`,
  setPaymentNotificationDialogTitle: 'Set Payment Notification',
  setPaymentNotificationDialogContent: 'Set a notification to remind you when the next payment is due.',
  notificationDueDateLabel: 'Notification Due Date',
} as const;

export default TEXTS;
